import { type AddToCartEventProps, buildAddToCartEvent } from './buildAddToCartEvent';
import { type AddToWishlistEventProps, buildAddToWishlistEvent } from './buildAddToWishlistEvent';
import { type BeginCheckoutEventProps, buildBeginCheckoutEvent } from './buildBeginCheckoutEvent';
import {
  type CustomPageViewEventProps,
  buildCustomPageViewEvent
} from './buildCustomPageViewEvent';
import { type FilterActionEventProps, buildFilterActionEvent } from './buildFilterActionEvent';
import {
  type NavigationClickEventProps,
  buildNavigationClickEvent
} from './buildNavigationClickEvent';
import { type PurchaseEventWithIdentityProps, buildPurchaseEvent } from './buildPurchaseEvent';
import { type SelectItemEventProps, buildSelectItemEvent } from './buildSelectItemEvent';
import { type ViewCartEventProps, buildViewCartEvent } from './buildViewCartEvent';
import { type ViewItemEventProps, buildViewItemEvent } from './buildViewItemEvent';
import { type ViewItemListEventProps, buildViewItemListEvent } from './buildViewItemListEvent';
import {
  type VirtualPageViewEventProps,
  buildVirtualPageViewEvent
} from './buildVirtualPageViewEvent';
import { setPendingBeginCheckout, takePendingBeginCheckout } from './pendingBeginCheckout';
import { buildECommerceResetEvent } from './buildECommerceResetEvent';
import { clearCatalogSource } from './catalogSourceStorage';
import { fireAndForgetExecuter } from '../utils/fireAndForgetExecuter';
import pushToDataLayer from './pushToDataLayer';

const deferBehindPageView = (callback: () => void): void => {
  fireAndForgetExecuter(() => {
    fireAndForgetExecuter(callback);
  });
};

const flushPendingBeginCheckout = (pageType: string | undefined): void => {
  const props = takePendingBeginCheckout();

  if (props !== null && pageType === 'checkout') {
    pushToDataLayer(buildECommerceResetEvent());
    pushToDataLayer(buildBeginCheckoutEvent(props));
  }
};

export const tracker = {
  customPageView(props: CustomPageViewEventProps): void {
    fireAndForgetExecuter(() => {
      const event = buildCustomPageViewEvent(props);

      if (event !== null) {
        pushToDataLayer(event);
      }
      flushPendingBeginCheckout(event?.page_type);
    });
  },
  virtualPageView(props: VirtualPageViewEventProps): void {
    fireAndForgetExecuter(() => {
      const event = buildVirtualPageViewEvent(props);
      if (event !== null) {
        pushToDataLayer(event);
      }
      flushPendingBeginCheckout(event?.page_type);
    });
  },
  navigationClick(props: NavigationClickEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildNavigationClickEvent(props));
    });
  },
  filterAction(props: FilterActionEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildFilterActionEvent(props));
    });
  },
  viewItem(props: ViewItemEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildViewItemEvent(props));
    });
  },
  viewItemList(props: ViewItemListEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildViewItemListEvent(props));
    });
  },
  addToWishlist(props: AddToWishlistEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildAddToWishlistEvent(props));
    });
  },
  selectItem(props: SelectItemEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildSelectItemEvent(props));
    });
  },
  purchase(props: PurchaseEventWithIdentityProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildPurchaseEvent(props));
      // Catalog attribution ends with the first conversion (ECOM-5701).
      clearCatalogSource();
    });
  },
  addToCart(props: AddToCartEventProps): void {
    fireAndForgetExecuter(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildAddToCartEvent(props));
    });
  },
  viewCart(props: ViewCartEventProps): void {
    deferBehindPageView(() => {
      pushToDataLayer(buildECommerceResetEvent());
      pushToDataLayer(buildViewCartEvent(props));
    });
  },
  beginCheckout(props: BeginCheckoutEventProps): void {
    setPendingBeginCheckout(props);
  }
};
